import { HEADER_MENU_DESKTOP_MIN, prefersReducedMotion } from '@theme/utilities';

const TYPE_DELAY_MS = 70;
const DELETE_DELAY_MS = 40;
const PAUSE_TYPED_MS = 2000;
const PAUSE_DELETED_MS = 400;
/* Header standalone hint uses header-chrome--menu-only (visible at ≥HEADER_MENU_DESKTOP_MIN + menu). Keep JS MQ in sync. */
const DESKTOP_MIN_WIDTH = `(min-width: ${HEADER_MENU_DESKTOP_MIN}px)`;

/**
 * Animated "Search: \"phrase\"" hint for predictive search inputs and header search triggers.
 * Animation runs only while the element is visible (IntersectionObserver) to avoid idle timers.
 * Weglot lang packs via `data-lang-packs` + `NoomaWeglotChrome.currentLang` (not Translate & Adapt / not `.wg-translate` on the typed span).
 *
 * @extends {HTMLElement}
 */
class DevSearchTypingHint extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;

  /** @type {HTMLElement | null} */
  #textEl = null;

  /** @type {HTMLElement | null} */
  #prefixEl = null;

  /** @type {string[]} */
  #phrases = [];

  /** @type {Record<string, { prefix?: string, phrases?: string[] }>} */
  #langPacks = {};

  /** @type {number} */
  #phraseIndex = 0;

  /** @type {number} */
  #charIndex = 0;

  /** @type {boolean} */
  #isDeleting = false;

  /** @type {ReturnType<typeof setTimeout> | null} */
  #timer = null;

  /** @type {boolean} */
  #isAnimating = false;

  /** @type {boolean} */
  #standalone = false;

  /** @type {boolean} */
  #isIntersecting = false;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {MediaQueryList} */
  #desktopQuery = window.matchMedia(DESKTOP_MIN_WIDTH);

  /** @type {boolean} */
  #weglotLanguageBound = false;

  connectedCallback() {
    this.#standalone = this.hasAttribute('data-standalone');
    const field = this.closest('[data-dev-search-typing-field]');
    this.#input = field?.querySelector('.search-input') ?? null;

    if (this.hasAttribute('hidden')) {
      return;
    }

    if (!this.#standalone && !this.#input) {
      return;
    }

    this.#prefixEl = this.querySelector('[data-typing-hint-prefix]');
    this.#textEl = this.querySelector('[data-typing-hint-text]');
    this.#parseLangPacks();
    this.#applyLangPack();

    if (!this.#textEl || this.#phrases.length === 0) {
      this.hidden = true;
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        this.#isIntersecting = entries[0]?.isIntersecting ?? false;
        this.#updateAnimation();
      },
      { threshold: 0 }
    );
    this.#observer.observe(this);

    if (this.#standalone) {
      this.#desktopQuery.addEventListener('change', this.#onDesktopQueryChange);
    } else {
      this.#input?.addEventListener('input', this.#updateAnimation);
    }

    window.addEventListener('weglot:ready', this.#onWeglotReady);
    this.#bindWeglotLanguageChanged();
  }

  disconnectedCallback() {
    this.#stopAnimation();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#input?.removeEventListener('input', this.#updateAnimation);
    this.#desktopQuery.removeEventListener('change', this.#onDesktopQueryChange);
    window.removeEventListener('weglot:ready', this.#onWeglotReady);
    if (this.#weglotLanguageBound && typeof window.Weglot?.off === 'function') {
      window.Weglot.off('languageChanged', this.#onWeglotLanguageChanged);
      this.#weglotLanguageBound = false;
    }
  }

  /** Parse `data-lang-packs`; fall back to EN `data-phrases`. */
  #parseLangPacks() {
    try {
      const parsed = JSON.parse(this.dataset.langPacks || '{}');
      this.#langPacks = parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      this.#langPacks = {};
    }

    if (Object.keys(this.#langPacks).length > 0) return;

    try {
      const phrases = JSON.parse(this.dataset.phrases || '[]');
      this.#langPacks = {
        en: {
          prefix: this.#prefixEl?.textContent?.trim() || '',
          phrases: Array.isArray(phrases) ? phrases : [],
        },
      };
    } catch {
      this.#langPacks = {};
    }
  }

  /** @returns {string} */
  #resolveLang() {
    const fromChrome = window.NoomaWeglotChrome?.currentLang?.();
    if (fromChrome) return fromChrome.toLowerCase();

    const fromWeglot = window.Weglot?.getCurrentLang?.();
    if (fromWeglot) return String(fromWeglot).toLowerCase();

    return 'en';
  }

  /**
   * Pick prefix + phrases for the displayed Weglot language (fallback `en`).
   */
  #applyLangPack() {
    const lang = this.#resolveLang();
    const pack = this.#langPacks[lang] || this.#langPacks.en;
    if (!pack) {
      this.#phrases = [];
      return;
    }

    const phrases = Array.isArray(pack.phrases)
      ? pack.phrases.map((phrase) => String(phrase).trim()).filter(Boolean)
      : [];
    this.#phrases = phrases;

    if (this.#prefixEl && typeof pack.prefix === 'string' && pack.prefix.trim()) {
      this.#prefixEl.textContent = pack.prefix.trim();
    }
  }

  #onWeglotReady = () => {
    this.#bindWeglotLanguageChanged();
    this.#reloadLangPack();
  };

  #onWeglotLanguageChanged = () => {
    this.#reloadLangPack();
  };

  #bindWeglotLanguageChanged() {
    if (this.#weglotLanguageBound) return;
    if (typeof window.Weglot?.on !== 'function') return;
    window.Weglot.on('languageChanged', this.#onWeglotLanguageChanged);
    this.#weglotLanguageBound = true;
  }

  #reloadLangPack() {
    const wasAnimating = this.#isAnimating;
    this.#applyLangPack();

    if (this.#phrases.length === 0 || !this.#textEl) {
      this.hidden = true;
      this.#stopAnimation();
      return;
    }

    if (wasAnimating) {
      this.#stopAnimation();
      this.#startAnimation();
    }
  }

  /**
   * Re-observe after breakpoint cross — CSS display:none → visible often skips IntersectionObserver.
   */
  #onDesktopQueryChange = () => {
    // Never leave [hidden] stuck: it zeros layout + IO forever, so desktop never recovers without refresh.
    if (this.#standalone && this.#desktopQuery.matches) {
      this.hidden = false;
    }

    if (this.#observer) {
      this.#observer.unobserve(this);
      this.#observer.observe(this);
    }

    // Double rAF: wait for compact/desktop CSS to apply before measuring layout.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.#updateAnimation();
      });
    });
  };

  #updateAnimation = () => {
    if (!this.#textEl || this.#phrases.length === 0) return;

    // Header hint: CSS (header-chrome--menu-only) owns visibility.
    // Do not set [hidden] on MQ miss — that blocks IO/rects after resize back to desktop.
    if (this.#standalone) {
      if (!this.#desktopQuery.matches) {
        this.#stopAnimation();
        return;
      }

      this.hidden = false;

      const layoutVisible = this.getClientRects().length > 0;
      if (this.#isIntersecting || layoutVisible) {
        if (!this.#isAnimating) {
          this.#startAnimation();
        }
        return;
      }

      this.#stopAnimation();
      return;
    }

    const layoutVisible = this.getClientRects().length > 0;
    const canRun =
      (this.#isIntersecting || layoutVisible) && (!this.#input || this.#input.value.length === 0);

    if (canRun) {
      this.hidden = false;

      if (!this.#isAnimating) {
        this.#startAnimation();
      }

      return;
    }

    this.hidden = true;
    this.#stopAnimation();
  };

  #startAnimation() {
    if (!this.#textEl || this.#phrases.length === 0) return;

    this.#isAnimating = true;

    if (prefersReducedMotion()) {
      this.#textEl.textContent = this.#phrases[0];
      return;
    }

    this.#phraseIndex = 0;
    this.#charIndex = 0;
    this.#isDeleting = false;
    this.#textEl.textContent = '';
    this.#scheduleTick(400);
  }

  #stopAnimation() {
    if (this.#timer) {
      clearTimeout(this.#timer);
      this.#timer = null;
    }

    this.#isAnimating = false;
  }

  /**
   * @param {number} delay
   */
  #scheduleTick(delay) {
    if (this.#timer) {
      clearTimeout(this.#timer);
    }

    this.#timer = setTimeout(this.#tick, delay);
  }

  #tick = () => {
    if (!this.#textEl || this.hidden) {
      this.#stopAnimation();
      return;
    }

    if (this.#standalone && !this.#desktopQuery.matches) {
      this.#stopAnimation();
      return;
    }

    // IO can lag one frame after unhiding; layout rects are enough to keep typing.
    if (!this.#isIntersecting && this.getClientRects().length === 0) {
      this.#stopAnimation();
      return;
    }

    if (!this.#standalone && this.#input && this.#input.value.length > 0) {
      this.#stopAnimation();
      return;
    }

    const currentPhrase = this.#phrases[this.#phraseIndex] ?? '';

    if (!this.#isDeleting) {
      this.#charIndex += 1;
      this.#textEl.textContent = currentPhrase.slice(0, this.#charIndex);

      if (this.#charIndex >= currentPhrase.length) {
        this.#scheduleTick(PAUSE_TYPED_MS);
        this.#isDeleting = true;
        return;
      }

      this.#scheduleTick(TYPE_DELAY_MS);
      return;
    }

    if (this.#charIndex > 0) {
      this.#charIndex -= 1;
      this.#textEl.textContent = currentPhrase.slice(0, this.#charIndex);
      this.#scheduleTick(DELETE_DELAY_MS);
      return;
    }

    this.#isDeleting = false;
    this.#phraseIndex = (this.#phraseIndex + 1) % this.#phrases.length;
    this.#scheduleTick(PAUSE_DELETED_MS);
  };
}

if (!customElements.get('dev-search-typing-hint')) {
  customElements.define('dev-search-typing-hint', DevSearchTypingHint);
}
